import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { usePageMeta } from '@/hooks/usePageMeta';

const TermsPage = () => {
  usePageMeta({
    title: 'Terms of Service | Talent Collective',
    description: 'Read our Terms of Service to understand your rights and responsibilities when using Talent Collective.'
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <Header />

      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Terms of Service</h1>
          <p className="text-gray-600">Last updated: February 2026</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl">
          <Card className="p-8 rounded-2xl border-gray-100">
            <div className="prose prose-stone max-w-none">
              <h2 className="text-xl font-bold text-stone-800 mb-4">1. Acceptance of Terms</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                By accessing and using Talent Collective ("the Platform"), you accept and agree to be bound by these Terms of Service. 
                The Platform is operated by ACT Capital Foundation For Social Impact in collaboration with The Product Folks.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">2. Platform Purpose</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                Talent Collective connects professionals with social impact organizations in the ACT Grants portfolio. 
                The Platform facilitates connections for fractional and project-based work opportunities focused on 
                education, healthcare, environment, and women's empowerment in India.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">3. User Accounts</h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                To use the Platform, you must:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be at least 18 years of age</li>
                <li>Not use the Platform for any unlawful purposes</li>
              </ul>

              <h2 className="text-xl font-bold text-stone-800 mb-4">4. User Conduct</h2>
              <p className="text-stone-600 mb-4 leading-relaxed">
                Users agree to:
              </p>
              <ul className="list-disc pl-6 text-stone-600 mb-6 space-y-2">
                <li>Provide truthful information in profiles and applications</li>
                <li>Respect intellectual property rights</li>
                <li>Not engage in discriminatory or harassing behavior</li>
                <li>Not misrepresent qualifications or experience</li>
              </ul>

              <h2 className="text-xl font-bold text-stone-800 mb-4">5. Content Ownership</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                Users retain ownership of content they submit to the Platform. By submitting content, 
                you grant the Platform a non-exclusive license to use, display, and share your content 
                for the purpose of operating the service.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">6. Limitation of Liability</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                The Platform serves as a connection service and is not a party to any agreements made 
                between talent and organizations. We are not responsible for the conduct of users or 
                the outcomes of any engagements facilitated through the Platform.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">7. Modifications</h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                We reserve the right to modify these Terms at any time. Users will be notified of 
                significant changes via email or through the Platform. Continued use after changes 
                constitutes acceptance of the modified terms.
              </p>

              <h2 className="text-xl font-bold text-stone-800 mb-4">8. Contact</h2>
              <p className="text-stone-600 leading-relaxed">
                For questions about these Terms, please contact us at{' '}
                <a href="mailto:legal@actgrants.in" className="text-teal-600 hover:underline">
                  legal@actgrants.in
                </a>
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
